import { useEffect, useState } from "react";

/**
 * useMediaQuery — subscribe to a CSS media query and re-render when it flips.
 *
 * Returns `true` when the query currently matches, `false` otherwise.
 *
 * Why we need this on top of Tailwind's `md:` visibility utilities:
 *   `hidden md:flex` still MOUNTS the subtree into React's DOM tree; only the
 *   pixels are hidden via CSS. On memory-constrained mobile Safari, mounting
 *   two full parallel lists (desktop table + mobile cards, ~900 items each)
 *   was enough to blow the tab's memory budget once the user started typing
 *   into the search box. Using this hook lets a component pick ONE branch to
 *   actually render.
 *
 * SSR-safe: falls back to `false` when `window` is missing.
 */
export default function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // Prime state on mount in case the query result changed between the
    // initial render and this effect (e.g. hydration mismatch).
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
